import type {
  AssignTicketBody,
  BulkTicketActionBody,
  CloseTicketBody,
} from '@planuze/admin-contracts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assignTicket,
  bulkTicketAction,
  closeTicket,
  getTicket,
  listTickets,
  type TicketsQuery,
} from '../lib/tickets-api.js';

/* Hooks TanStack Query do domínio tickets (ADR-0352). Mutações invalidam o detail +
   a lista (status/assignee mudam). */

/* Polling da listagem de tickets: não há canal realtime na lista (o WS é por-ticket,
   ADR-0433). Refetch periódico + on-focus faz tickets novos (criados pelo app) e
   mudanças de status aparecerem sem o staff recarregar a página. */
const TICKETS_LIST_POLL_MS = 10_000;

export function useTicketsList(filters: TicketsQuery) {
  return useInfiniteQuery({
    queryKey: ['tickets', 'list', filters],
    queryFn: ({ pageParam }) => listTickets({ ...filters, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: TICKETS_LIST_POLL_MS,
    refetchOnWindowFocus: true,
  });
}

export function useTicketDetail(id: string) {
  return useQuery({ queryKey: ['tickets', 'detail', id], queryFn: () => getTicket(id) });
}

function useInvalidateTicket(id: string): () => void {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['tickets', 'detail', id] });
    queryClient.invalidateQueries({ queryKey: ['tickets', 'list'] });
  };
}

export function useAssignTicket(id: string) {
  const invalidate = useInvalidateTicket(id);
  return useMutation({
    mutationFn: (body: AssignTicketBody) => assignTicket(id, body),
    onSuccess: invalidate,
  });
}

export function useCloseTicket(id: string) {
  const invalidate = useInvalidateTicket(id);
  return useMutation({
    mutationFn: (body: CloseTicketBody) => closeTicket(id, body),
    onSuccess: invalidate,
  });
}

export function useBulkTicketAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkTicketActionBody) => bulkTicketAction(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tickets', 'list'] }),
  });
}
